import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { appSettings } from 'src/app/app.settings';
import { LOCAL_STORAGE_KEY } from 'src/app/config';
import { ReportFileType, SalesReportViewType } from 'src/app/enum';
import { isSalesReportViewable } from 'src/app/enum/report-status';
import { LocalStorage, LxmDialog, ContentLanguage } from 'src/app/_helpers';
import { SalesReportExpandDialog } from 'src/app/cards/reports/sales-report/sales-report-expand/sales-report-expand.dialog';
import { toReportDay, toReportDayIso } from 'src/app/cards/reports/reports-date.utils';
import {
  ILocationSimple,
  ISalesReportDto,
  ISalesReportFormData,
  ISalesReportSearchData,
  ReportsService,
  SalesReportInventoryFilter,
  SalesReportPriceType
} from 'src/app/_services/reports.service';

@Component({
  selector: 'sales-report-search-card',
  templateUrl: './sales-report-search.card.html',
  host: { class: 'sales-report-search-card' },
  styleUrls: ['./sales-report-search.card.scss']
})
export class SalesReportSearchCard implements OnInit, OnDestroy {
  @Input() public isOpen = true;
  @Input() public localStorageKey = LOCAL_STORAGE_KEY.REPORTS_SEARCH_PANEL_OPEN;
  @Input() public canCreate = false;
  @Input() public canManage = false;
  @Output() public search = new EventEmitter<ISalesReportSearchData>();
  @Output() public loadingChange = new EventEmitter<boolean>();

  public formData: ISalesReportFormData;
  public providerId: string | null;
  public currentReport: ISalesReportDto | null;
  public locationOptions: ILocationSimple[] = [];
  public minDate: moment.Moment;
  public maxDate: moment.Moment;
  public isLoading = false;
  public isReady = false;

  public searchData = new FormGroup({
    searchTerm: new FormControl(null),
    from: new FormControl(null),
    to: new FormControl(null),
    viewType: new FormControl(null),
    fileType: new FormControl([
      ReportFileType.Selver_SalesReport_SalesFile,
      ReportFileType.Selver_SalesReport_InventoryFile
    ]),
    locations: new FormControl([]),
    product: new FormControl(null),
    inventoryFilter: new FormControl(SalesReportInventoryFilter.All),
    priceType: new FormControl(SalesReportPriceType.Final)
  });

  public SalesReportViewType = SalesReportViewType;
  public SalesReportInventoryFilter = SalesReportInventoryFilter;
  public SalesReportPriceType = SalesReportPriceType;

  public periodPresets = [7, 14, 28, 35];

  public fileTypeOptions = [
    {
      id: ReportFileType.Selver_SalesReport_SalesFile,
      name: 'cards.reports.sales_report_search.sales_data',
      icon: 'report-sales'
    },
    {
      id: ReportFileType.Selver_SalesReport_InventoryFile,
      name: 'cards.reports.sales_report_search.inventory',
      icon: 'report-inventory'
    }
  ];

  private _createSub: Subscription;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _reportsService: ReportsService,
    private _dialog: LxmDialog,
    private _contentLanguage: ContentLanguage
  ) {
    this.providerId = this._route.snapshot.paramMap.get('providerId');
    this.formData = this._route.snapshot.data.formData as ISalesReportFormData;
    this.currentReport = (this._route.snapshot.data.report as ISalesReportDto) || null;
    this.isReady = isSalesReportViewable(this.currentReport?.status);
    if (this.currentReport?.formData) {
      this.formData = this.currentReport.formData;
    }
    this.locationOptions = this.formData?.locations || [];

    if (this.formData?.minReportDate) {
      this.minDate = toReportDay(this.formData.minReportDate);
    }
    if (this.formData?.maxReportDate) {
      this.maxDate = toReportDay(this.formData.maxReportDate);
    }
  }

  public ngOnInit() {
    if (!this.isReady) {
      this.isOpen = true;
    }

    this.searchData.patchValue(
      { viewType: SalesReportViewType.ProductByDay },
      { emitEvent: false }
    );

    if (this.currentReport) {
      this.applyReport(this.currentReport);
    } else {
      const availableFileTypes = this.formData?.fileTypes?.length
        ? this.fileTypeOptions.filter((x) => this.formData.fileTypes.includes(x.id))
        : this.fileTypeOptions;

      this.searchData.patchValue(
        { fileType: availableFileTypes.map((x) => x.id) },
        { emitEvent: false }
      );
      this.applyPeriodPreset(7);
    }

    this.emitSearch();
  }

  public ngOnDestroy() {
    this._createSub?.unsubscribe();
  }

  public toggleOpen(isOpen: boolean) {
    this.isOpen = isOpen;
    LocalStorage.set(this.localStorageKey, isOpen);
  }

  public applyPeriodPreset(days: number) {
    if (this.isLoading) {
      return;
    }

    const range = this.periodPresetRange(days);
    let from = range.from.clone();
    if (this.minDate && from.isBefore(this.minDate)) {
      from = this.minDate.clone().startOf('day');
    }

    this.searchData.patchValue({
      from,
      to: range.to
    });
  }

  public isPeriodPresetActive(days: number): boolean {
    const range = this.periodPresetRange(days);
    const from = toReportDay(this.searchData.get('from')?.value);
    const to = toReportDay(this.searchData.get('to')?.value);

    if (!from || !to) {
      return false;
    }

    if (this.minDate && range.from.isBefore(this.minDate, 'day')) {
      return false;
    }

    return from.isSame(range.from, 'day') && to.isSame(range.to, 'day');
  }

  private periodPresetRange(days: number) {
    const to = (this.maxDate ? this.maxDate.clone() : moment.utc()).startOf('day');
    return {
      from: to.clone().subtract(days - 1, 'days'),
      to
    };
  }

  public setViewType(viewType: SalesReportViewType) {
    if (this.isLoading) {
      return;
    }

    this.searchData.patchValue({ viewType });
  }

  public get searchPlaceholder(): string {
    return this.currentViewType === SalesReportViewType.LocationByDay
      ? 'cards.reports.sales_report_search.placeholder_stores'
      : 'cards.reports.sales_report_search.placeholder';
  }

  public get currentViewType(): SalesReportViewType {
    const value = this.searchData.get('viewType')?.value;
    return value;
  }

  public get isLocationView(): boolean {
    return this.currentViewType === SalesReportViewType.LocationByDay;
  }

  public productAcLabel = (item: any) => {
    const name = item?.name || item?.productFullName || item?.productFullNameTranslated;
    if (!name) {
      return '';
    }
    if (typeof name === 'string') {
      return name;
    }
    return this._contentLanguage.get(name) || '';
  };

  public toggleFileType(fileType: ReportFileType) {
    if (this.isLoading) {
      return;
    }

    const current: ReportFileType[] = [
      ...(this.searchData.get('fileType').value || [])
    ];
    const index = current.indexOf(fileType);

    if (index >= 0) {
      if (current.length === 1) {
        return;
      }
      current.splice(index, 1);
    } else {
      current.push(fileType);
    }

    this.searchData.patchValue({ fileType: current });
  }

  public hasFileType(fileType: ReportFileType): boolean {
    return (this.searchData.get('fileType').value || []).includes(fileType);
  }

  public setInventoryFilter(filter: SalesReportInventoryFilter) {
    if (this.isLoading) {
      return;
    }

    this.searchData.patchValue({ inventoryFilter: filter });
  }

  public setPriceType(priceType: SalesReportPriceType) {
    if (this.isLoading) {
      return;
    }

    this.searchData.patchValue({ priceType });
  }

  public submit() {
    if (this.isLoading) {
      return;
    }

    if (!this.currentReport) {
      if (!this.canCreate) {
        return;
      }
      this.createReport();
      return;
    }

    this.emitSearch();
  }

  public openExpandDialog() {
    if (!this.isReady || !this.canManage || this.isLoading || !this.currentReport) {
      return;
    }

    this._dialog.open(
      SalesReportExpandDialog,
      {
        width: appSettings.DIALOG_WIDTHS.M,
        data: {
          report: this.currentReport,
          formData: this.currentReport.formData || this.formData
        }
      },
      (result) => {
        if (!result) {
          return;
        }

        this.searchData.patchValue(
          {
            from: result.from,
            to: result.to,
            fileType: result.fileType,
            locations: (result.locations || [])
              .map((x: ILocationSimple | string) => (typeof x === 'string' ? x : x?.id))
              .filter(Boolean)
          },
          { emitEvent: false }
        );
        this.isReady = false;
        this.searchData.disable({ emitEvent: false });
        this.setLoading(true);
        this.emitSearch();
      }
    );
  }

  private createReport() {
    if (!this.canCreate) {
      return;
    }

    const serialized = this.serializeSearchData();
    this.setLoading(true);
    this.searchData.disable({ emitEvent: false });
    this.emitSearch();

    this._createSub?.unsubscribe();
    this._createSub = this._reportsService
      .createSalesReport({
        providerId: this.providerId,
        from: toReportDayIso(serialized.from) || undefined,
        to: toReportDayIso(serialized.to) || undefined,
        fileTypes: serialized.fileTypes,
        locationIds: serialized.locationIds
      })
      .subscribe({
        next: (id) => {
          const reportId = typeof id === 'string' ? id : (id as any)?.id;
          if (!reportId) {
            this.searchData.enable({ emitEvent: false });
            this.setLoading(false);
            return;
          }

          this._router.navigate(['/reports/sales', this.providerId, reportId]);
        },
        error: () => {
          this.searchData.enable({ emitEvent: false });
          this.setLoading(false);
        }
      });
  }

  private applyReport(report: ISalesReportDto) {
    this.setLoading(!this.isReady);

    const selected = report.selectedDataFilters;
    if (report.formData) {
      this.formData = report.formData;
      this.locationOptions = report.formData.locations || this.locationOptions;
    }

    this.searchData.patchValue(
      {
        from: toReportDay(selected?.from),
        to: toReportDay(selected?.to),
        fileType: selected?.fileTypes?.length
          ? selected.fileTypes
          : this.searchData.get('fileType')?.value,
        locations: (selected?.locations || [])
          .map((x) => (typeof x === 'string' ? x : x?.id))
          .filter(Boolean)
      },
      { emitEvent: false }
    );

    if (!this.isReady) {
      this.searchData.disable({ emitEvent: false });
    }
  }

  private emitSearch() {
    this.search.emit(this.serializeSearchData());
  }

  public enterGeneratingState() {
    this.isOpen = true;
    this.isReady = false;
    this.searchData.disable({ emitEvent: false });
    this.setLoading(true);
  }

  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
    this.loadingChange.emit(isLoading);
  }

  private serializeSearchData(): ISalesReportSearchData {
    const value = this.searchData.getRawValue();
    const viewType = value.viewType;
    const productId = this.toProductId(value.product);

    return {
      searchTerm: value.searchTerm,
      from: value.from,
      to: value.to,
      viewType,
      fileTypes: value.fileType || [],
      locationIds:
        viewType === SalesReportViewType.LocationByDay
          ? undefined
          : (value.locations || []).map((x: ILocationSimple | string) =>
              typeof x === 'string' ? x : x?.id
            ),
      locations:
        viewType === SalesReportViewType.LocationByDay
          ? []
          : this.resolveLocations(value.locations),
      productIds:
        viewType === SalesReportViewType.LocationByDay && productId
          ? [productId]
          : undefined,
      productLabel:
        viewType === SalesReportViewType.LocationByDay
          ? this.productAcLabel(value.product) || undefined
          : undefined,
      inventoryFilter: value.inventoryFilter,
      priceType: value.priceType
    };
  }

  private toProductId(product: any): string | null {
    if (!product) {
      return null;
    }
    return typeof product === 'string' ? product : product.id || null;
  }

  private resolveLocations(locations: (ILocationSimple | string)[]): ILocationSimple[] {
    return (locations || [])
      .map((x) => {
        if (!x) {
          return null;
        }
        if (typeof x !== 'string') {
          return x;
        }
        return this.locationOptions.find((o) => o.id === x) || null;
      })
      .filter((x): x is ILocationSimple => !!x);
  }
}
